/**
 * Phase 27.45 — Event Requests tab. Lives inside Marketing Center.
 *
 * Provides list + filters + a New Event Request CTA. Row click opens a
 * detail dialog with the approval workflow actions. The dialog is
 * kept in this file (co-located) to avoid a proliferation of files
 * for a single-tab flow — extraction can happen later if it grows.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Loader2, Search, Filter, CalendarRange, DollarSign, Users, ExternalLink,
} from 'lucide-react';
import { eventRequestsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import EventRequestFormDialog from './EventRequestFormDialog';
import EventRequestDetailDialog from './EventRequestDetailDialog';
import LiveCaptureButton from '@/components/agent-events/LiveCaptureButton';

const STATUS_META = {
  submitted:           { label: 'Submitted',       tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  agency_review:       { label: 'Agency Review',   tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  kr_review:           { label: 'KR Review',       tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  needs_information:   { label: 'Needs Info',      tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved:            { label: 'Approved',        tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  partially_approved:  { label: 'Partially Appr.', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  declined:            { label: 'Declined',        tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  cancelled:           { label: 'Cancelled',       tone: 'bg-slate-100 text-slate-500 border-slate-300 line-through' },
  completed:           { label: 'Completed',       tone: 'bg-slate-200 text-slate-700 border-slate-300' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: 'bg-slate-100 text-slate-700' };
  return (
    <Badge variant="outline" className={`${meta.tone} text-[11px] font-medium`}>
      {meta.label}
    </Badge>
  );
}

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function EventRequestsTab() {
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState(null); // request_id being viewed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await eventRequestsAPI.list(params);
      setRows(r.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load event requests');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { count: rows.length, requested: 0, approved: 0 };
    rows.forEach((r) => {
      t.requested += Number(r.amount_requested || 0);
      t.approved += Number(r.amount_approved || 0);
    });
    return t;
  }, [rows]);

  return (
    <div data-testid="event-requests-tab" className="space-y-5">
      {/* Filter bar + New Request button */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search event name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="event-requests-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="event-requests-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40" data-testid="event-requests-from-date"
            title="From event date"
          />
          <Input
            type="date" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40" data-testid="event-requests-to-date"
            title="To event date"
          />
        </div>
        <div className="flex items-center gap-2">
          <LiveCaptureButton size="md" />
          <Button
            onClick={() => setOpenForm(true)}
            data-testid="event-request-new-btn"
          >
            <Plus size={16} className="mr-2" /> New Event Request
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Events</p>
              <p className="text-2xl font-bold text-slate-900">{totals.count}</p>
            </div>
            <CalendarRange className="text-slate-300" size={28} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Requested</p>
              <p className="text-2xl font-bold text-slate-900">{fmtMoney(totals.requested)}</p>
            </div>
            <DollarSign className="text-slate-300" size={28} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Approved</p>
              <p className="text-2xl font-bold text-emerald-700">{fmtMoney(totals.approved)}</p>
            </div>
            <DollarSign className="text-emerald-300" size={28} />
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="animate-spin text-slate-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Filter className="mx-auto mb-3 text-slate-300" size={32} />
              <p className="text-slate-500 font-medium">No event requests match your filters</p>
              <p className="text-sm text-slate-400 mt-1">
                Click &ldquo;New Event Request&rdquo; to submit the first one.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="event-requests-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Event</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Requester</th>
                    <th className="px-4 py-3 font-semibold text-right">Requested</th>
                    <th className="px-4 py-3 font-semibold text-right">Approved</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Agents</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr
                      key={r.request_id}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelected(r.request_id)}
                      data-testid={`event-request-row-${r.request_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[220px]">
                          {r.event_name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[220px]">
                          {r.venue || '—'}{r.city ? `, ${r.city}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.event_date)}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-800">{r.requester_name}</div>
                        <div className="text-[11px] text-slate-500 capitalize">
                          {(r.requester_role || '').replace('_', ' ')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{fmtMoney(r.amount_requested)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-700">
                        {fmtMoney(r.amount_approved)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 text-slate-600">
                          <Users size={12} />
                          {(r.participating_agents || []).length}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ExternalLink size={14} className="text-slate-400 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EventRequestFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        onSaved={() => { setOpenForm(false); load(); }}
      />
      <EventRequestDetailDialog
        requestId={selected}
        isKR={isKR}
        onOpenChange={(v) => { if (!v) setSelected(null); }}
        onChanged={load}
      />
    </div>
  );
}
